/**
 * Bundle Discount utilities.
 *
 * Discount when buying multiple items/nights together.
 *
 * Tiers stored in listing.publicData.bundleDiscount = [
 *   { minQuantity: 2, discountPercent: 5 },
 *   { minQuantity: 5, discountPercent: 10 },
 *   { minQuantity: 10, discountPercent: 15 },
 * ]
 *
 * The highest applicable tier wins.
 */

/**
 * Get the applicable bundle discount tier for a quantity.
 * @param {Object[]} tiers — sorted by minQuantity ascending
 * @param {number} quantity — number of items/nights
 * @returns {{ minQuantity: number, discountPercent: number } | null}
 */
export const getApplicableTier = (tiers, quantity) => {
  if (!Array.isArray(tiers) || tiers.length === 0 || quantity < 1) return null;

  const sorted = [...tiers].sort((a, b) => b.minQuantity - a.minQuantity);
  return sorted.find(tier => quantity >= tier.minQuantity) || null;
};

/**
 * Calculate bundle discount.
 * @param {number} unitPriceCents
 * @param {number} quantity
 * @param {Object[]} tiers
 * @returns {{ tier: Object|null, discountCents: number, totalCents: number, perUnitCents: number }}
 */
export const calculateBundleDiscount = (unitPriceCents, quantity, tiers) => {
  const subtotal = unitPriceCents * quantity;
  const tier = getApplicableTier(tiers, quantity);

  if (!tier) return { tier: null, discountCents: 0, totalCents: subtotal, perUnitCents: unitPriceCents };

  const discountCents = Math.round(subtotal * (tier.discountPercent / 100));
  const totalCents = subtotal - discountCents;
  return { tier, discountCents, totalCents, perUnitCents: Math.round(totalCents / quantity) };
};

/**
 * Validate a bundle discount tier array.
 * @param {Object[]} tiers
 * @returns {string|null}
 */
export const validateTiers = (tiers) => {
  if (!Array.isArray(tiers)) return 'Tiers must be an array';
  for (const tier of tiers) {
    if (!tier.minQuantity || tier.minQuantity < 2) return 'Each tier needs minQuantity >= 2';
    if (!tier.discountPercent || tier.discountPercent < 1 || tier.discountPercent > 100) {
      return 'discountPercent must be 1-100';
    }
  }
  return null;
};
